import { useDeferredValue, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { Activity, Columns3, Database, Search, Table2, Wifi, WifiOff } from 'lucide-react'
import { createLiveOddsVnextStore } from '../lib/liveOddsVnextClient.js'
import { LIVE_ODDS_ENVIRONMENT } from '../lib/liveOddsVnextContract.js'
import { buildLiveOddsMatrix } from '../lib/liveOddsMatrixModelV1.js'
import { buildLiveOddsEventBoard } from '../lib/liveOddsEventBoardModelV1.js'
import { sortTerminalEvents } from '../lib/liveOddsMarketTerminalModelV1.js'
import {
  filterOfferRows,
  groupExactOffers,
  summarizeRows,
} from '../lib/liveOddsVnextModel.js'
import { pageOddsItems } from '../lib/liveOddsVnextPresentationContract.js'
import { resolveBookIdentity } from '../lib/bookLogoRegistry.js'
import LiveOddsVNextExactTable from './LiveOddsVNextExactTable.jsx'
import LiveOddsVNextEventBoard from './LiveOddsVNextEventBoard.jsx'
import LiveOddsVNextFormulaBar from './LiveOddsVNextFormulaBar.jsx'
import LiveOddsVNextBookUniverse from './LiveOddsVNextBookUniverse.jsx'
import LiveOddsVNextMatrix from './LiveOddsVNextMatrix.jsx'
import LiveOddsVNextOfferDrawer from './LiveOddsVNextOfferDrawer.jsx'
import AbetChipLoader from './shared/AbetChipLoader.jsx'
import './LiveOddsVNextWidget.css'

const LIVE_ODDS_TERMINAL_VERSION = '20260822.1.0.0'
const IS_PRODUCTION = LIVE_ODDS_ENVIRONMENT === 'production'
const SURFACE_KICKER = IS_PRODUCTION ? 'live production terminal' : 'development proof'
const SURFACE_HEADING = IS_PRODUCTION ? 'Married Live Odds Market Terminal' : 'Married Live Odds Development Board'
const EMPTY_STATE = Object.freeze({})
let sharedDefaultStore = null

function defaultStore() {
  sharedDefaultStore ||= createLiveOddsVnextStore()
  return sharedDefaultStore
}

function rowsFromState(state) {
  const candidates = [state.rows, state.categoryRows, state.category?.rows, state.data?.rows]
  return candidates.find(Array.isArray) || []
}

function categoryOptions(state) {
  const source = state.categoryManifest?.categories
    || state.manifest?.categories
    || state.categories
    || []
  const artifacts = state.releaseManifest?.artifacts || {}
  return (Array.isArray(source) ? source : []).map((entry) => {
    const name = entry.name || entry.category || entry.label || entry.slug || 'Unknown'
    const slug = entry.slug || entry.category_slug || String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-')
    return {
      slug,
      name,
      rowCount: entry.n_rows ?? entry.row_count ?? entry.rows ?? entry.count ?? null,
      available: Boolean(artifacts[`cat_${slug}.json`]),
    }
  })
}

function formatAge(value, tick) {
  void tick
  const parsed = Date.parse(value || '')
  if (!Number.isFinite(parsed)) return 'unknown'
  const seconds = Math.max(0, Math.floor((Date.now() - parsed) / 1000))
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
}

function stateFact(state, names, fallback = null) {
  for (const name of names) {
    const value = name.split('.').reduce((node, key) => node?.[key], state)
    if (value !== null && value !== undefined && value !== '') return value
  }
  return fallback
}

function StatusRail({ state, summary, tick }) {
  const http = stateFact(state, ['httpStatus', 'bootstrapStatus', 'http.status'], 'idle')
  const ws = stateFact(state, ['wsStatus', 'streamStatus', 'websocket.status', 'connectionState'], 'disconnected')
  const release = stateFact(state, ['releaseId', 'release.release_id', 'manifest.release_id'], 'unknown')
  const sequence = stateFact(state, ['sequence', 'websocket.sequence'], 'unknown')
  const captured = stateFact(state, ['capturedAt', 'capturedUtc', 'captured_utc', 'release.captured_utc', 'manifest.captured_utc'])
  const deliveryMode = stateFact(state, ['deliveryMode'], 'pending')
  const wsOk = /open|connected|streaming|live|recovering/i.test(String(ws))
  const degraded = Boolean(state.degradedReason || state.error || !wsOk)
  const integrity = state.unverifiedArtifact
    ? 'UNVERIFIED'
    : state.artifactVerification?.sha256 && state.categoryManifestVerification?.sha256
      ? 'SHA-256 VERIFIED'
      : 'PENDING'
  return (
    <section className="lovx-status-rail" aria-label="Live odds transport status">
      <div className={`lovx-status-cell ${degraded ? 'warn' : 'ok'}`}>
        {wsOk ? <Wifi size={15} /> : <WifiOff size={15} />}
        <span>WebSocket</span><strong>{String(ws)}</strong>
      </div>
      <div className="lovx-status-cell"><Database size={15} /><span>HTTP bootstrap</span><strong>{String(http)}</strong></div>
      <div className="lovx-status-cell"><Activity size={15} /><span>Capture age</span><strong>{formatAge(captured, tick)}</strong></div>
      <div className="lovx-status-cell"><span>Release</span><code title={String(release)}>{String(release).slice(0, 12)}</code></div>
      <div className="lovx-status-cell"><span>Sequence</span><strong>{String(sequence)}</strong></div>
      <div className="lovx-status-cell"><span>Rows / books</span><strong>{summary.rowCount.toLocaleString()} / {summary.bookCount}</strong></div>
      <div className="lovx-status-cell"><span>Payload transport</span><strong>{deliveryMode === 'r2' ? 'R2 partitions' : deliveryMode === 'v1' ? 'signed shard' : String(deliveryMode)}</strong></div>
      <div className={`lovx-status-cell ${integrity === 'SHA-256 VERIFIED' ? 'ok' : 'warn'}`}><span>Artifact</span><strong>{integrity}</strong></div>
      <div className="lovx-status-cell"><span>Environment</span><strong>{LIVE_ODDS_ENVIRONMENT}</strong></div>
      {state.reconnectAttempt > 0 && <div className="lovx-status-cell warn"><span>Reconnect</span><strong>attempt {state.reconnectAttempt}</strong></div>}
      {state.degradedReason && <div className="lovx-status-cell warn"><span>Degraded reason</span><strong title={state.degradedReason}>{state.degradedReason}</strong></div>}
    </section>
  )
}

function Filters({ categories, selectedCategory, setCategory, summary, filters, setFilters, oddsFormat, setOddsFormat }) {
  const update = (key) => (event) => setFilters((current) => ({ ...current, [key]: event.target.value }))
  return (
    <section className="lovx-filters" aria-label="Live odds filters">
      <label>Category<select value={selectedCategory} onChange={(event) => setCategory(event.target.value)}>
        {categories.map((item) => <option key={item.slug} value={item.slug} disabled={!item.available}>{item.name}{item.rowCount == null ? '' : ` (${item.rowCount.toLocaleString()})`}{item.available ? '' : ' · unavailable in current release'}</option>)}
      </select></label>
      <label>League<select value={filters.league} onChange={update('league')}><option value="ALL">All</option>{summary.leagues.map((value) => <option key={value}>{value}</option>)}</select></label>
      <label>Market<select value={filters.betType} onChange={update('betType')}><option value="ALL">All</option>{summary.betTypes.map((value) => <option key={value}>{value}</option>)}</select></label>
      <label>Sportsbook<select value={filters.books} onChange={update('books')}><option value="ALL">All</option>{summary.books.map((book) => <option key={book} value={book}>{resolveBookIdentity(book).displayName}</option>)}</select></label>
      <label className="lovx-search"><Search size={14} /><input value={filters.search} onChange={update('search')} placeholder="Event, selection, book…" /></label>
      <div className="lovx-segmented" aria-label="Odds format">
        {['american', 'decimal'].map((value) => <button type="button" className={oddsFormat === value ? 'active' : ''} onClick={() => setOddsFormat(value)} key={value}>{value === 'american' ? 'US' : 'DEC'}</button>)}
      </div>
    </section>
  )
}

function PageControls({ page, pageCount, pageSize, itemCount, setPage }) {
  if (pageCount <= 1) return null
  const start = page * pageSize + 1
  const end = Math.min(itemCount, start + pageSize - 1)
  return (
    <nav className="lovx-pagination" aria-label="Odds result pages">
      <button type="button" disabled={page === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}>Previous</button>
      <span>{start.toLocaleString()}-{end.toLocaleString()} of {itemCount.toLocaleString()} · page {(page + 1).toLocaleString()} / {pageCount.toLocaleString()}</span>
      <button type="button" disabled={page + 1 >= pageCount} onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))}>Next</button>
    </nav>
  )
}

export default function LiveOddsVNextWidget({ store = null, initialCategory = 'game-lines' }) {
  const storeRef = useRef(store || defaultStore())
  const currentStore = storeRef.current
  const state = useSyncExternalStore(
    (listener) => currentStore.subscribe(listener),
    () => currentStore.getState(),
    () => EMPTY_STATE,
  )
  const [view, setView] = useState('board')
  const [category, setCategory] = useState(initialCategory)
  const [oddsFormat, setOddsFormat] = useState('american')
  const [filters, setFilters] = useState({ league: 'ALL', betType: 'ALL', books: 'ALL', search: '' })
  const [fairSettings, setFairSettings] = useState({ method: 'multiplicative', weighting: 'equal' })
  const [selectedBooks, setSelectedBooks] = useState(null)
  const [selectedRow, setSelectedRow] = useState(null)
  const [page, setPage] = useState(0)
  const [tick, setTick] = useState(0)
  const boardAvailable = category === 'game-lines'

  useEffect(() => {
    currentStore.start()
    return () => currentStore.stop()
  }, [currentStore])

  useEffect(() => {
    Promise.resolve(currentStore.selectCategory(category)).catch(() => {})
  }, [category, currentStore])

  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), 10_000)
    return () => clearInterval(timer)
  }, [])

  const rows = rowsFromState(state)
  const deferredSearch = useDeferredValue(filters.search)
  const effectiveFilters = useMemo(() => ({
    league: filters.league,
    betType: filters.betType,
    books: filters.books,
    search: deferredSearch,
  }), [filters.league, filters.betType, filters.books, deferredSearch])
  const unfilteredSummary = useMemo(() => summarizeRows(rows), [rows])
  const filteredByControls = useMemo(() => filterOfferRows(rows, effectiveFilters), [rows, effectiveFilters])
  const filteredRows = useMemo(() => {
    if (selectedBooks == null) return filteredByControls
    const selected = new Set(selectedBooks)
    return filteredByControls.filter((row) => selected.has(row.book))
  }, [filteredByControls, selectedBooks])
  const filteredSummary = useMemo(() => summarizeRows(filteredRows), [filteredRows])
  const exactGroups = useMemo(() => view === 'exact' ? groupExactOffers(filteredRows) : [], [filteredRows, view])
  const matrix = useMemo(() => view === 'matrix' || view === 'board'
    ? buildLiveOddsMatrix(filteredRows, fairSettings)
    : { events: [], books: [], counts: { events: 0, markets: 0, rows: 0 } }, [filteredRows, fairSettings, view])
  const board = useMemo(() => view === 'board'
    ? buildLiveOddsEventBoard(matrix)
    : { events: [], counts: { events: 0, moneylineEvents: 0, primaryMarkets: 0, alternateMarkets: 0 } }, [matrix, view])
  const terminalEvents = useMemo(() => sortTerminalEvents(board.events), [board.events])
  const matrixItems = useMemo(() => matrix.events.flatMap((event) => event.markets.map((market) => ({ event, market }))), [matrix])
  const items = view === 'board' ? terminalEvents : view === 'matrix' ? matrixItems : exactGroups
  const paged = pageOddsItems(items, view, page)
  const { items: visibleItems, page: safePage, pageCount, pageSize } = paged
  const loading = state.loading || state.httpStatus === 'loading' || state.httpStatus === 'bootstrapping'
  const categories = useMemo(() => {
    const values = categoryOptions(state)
    return values.length > 0 ? values : [{ slug: category, name: category, rowCount: rows.length, available: true }]
  }, [state, category, rows.length])

  useEffect(() => setPage(0), [view, category, effectiveFilters])
  useEffect(() => setSelectedRow(null), [category, state.releaseId])
  useEffect(() => {
    if (!boardAvailable && view === 'board') setView('matrix')
  }, [boardAvailable, view])
  const switchCategory = (slug) => {
    setCategory(slug)
    setSelectedRow(null)
  }
  return (
    <div className="lovx-shell">
      <header className="lovx-heading">
        <div><p>ABET market fabric · {SURFACE_KICKER} · {LIVE_ODDS_TERMINAL_VERSION}</p><h2>{SURFACE_HEADING}</h2></div>
        <div className="lovx-view-toggle">
          <button type="button" disabled={!boardAvailable} title={boardAvailable ? 'Dense customer odds screen' : 'Odds Screen is defined for Game Lines; All Markets retains this category.'} className={view === 'board' ? 'active' : ''} onClick={() => setView('board')}><Activity size={14} /> Odds Screen</button>
          <button type="button" className={view === 'matrix' ? 'active' : ''} onClick={() => setView('matrix')}><Columns3 size={14} /> All Markets</button>
          <button type="button" className={view === 'exact' ? 'active' : ''} onClick={() => setView('exact')}><Table2 size={14} /> Raw Proof</button>
        </div>
      </header>
      <StatusRail state={state} summary={unfilteredSummary} tick={tick} />
      <Filters categories={categories} selectedCategory={category} setCategory={switchCategory} summary={unfilteredSummary} filters={filters} setFilters={setFilters} oddsFormat={oddsFormat} setOddsFormat={setOddsFormat} />
      {(view === 'matrix' || view === 'board') && <LiveOddsVNextFormulaBar settings={fairSettings} setSettings={setFairSettings} />}
      {(view === 'matrix' || view === 'board') && <LiveOddsVNextBookUniverse books={unfilteredSummary.books} selectedBooks={selectedBooks} setSelectedBooks={setSelectedBooks} weighting={fairSettings.weighting} />}
      {state.error && <div className="lovx-alert"><strong>Data plane degraded.</strong> {String(state.error.message || state.error)}</div>}
      <section className="lovx-accounting">
        <span>{filteredSummary.rowCount.toLocaleString()} of {unfilteredSummary.rowCount.toLocaleString()} raw rows visible</span>
        <span>{filteredSummary.eventCount.toLocaleString()} events · {filteredSummary.exactOfferCount.toLocaleString()} exact offer groups</span>
        {view === 'board' && <span>{board.counts.moneylineEvents.toLocaleString()} / {board.counts.events.toLocaleString()} events with captured Moneyline</span>}
        {view === 'board' && <span>{board.counts.primaryMarkets.toLocaleString()} primary · {board.counts.alternateMarkets.toLocaleString()} additional market families retained</span>}
        {view === 'matrix' && <span>{matrix.counts.markets.toLocaleString()} married market families · bounded to {pageSize} families per render</span>}
        {loading && rows.length > 0 && <span className="lovx-refreshing">Refreshing in background · last verified rows remain visible</span>}
      </section>
      {loading && rows.length === 0 ? <div className="lovx-empty"><AbetChipLoader message="Loading signed odds release..." /></div>
        : items.length === 0 ? <div className="lovx-empty">No rows match this category/filter combination. The category remains visible because historical availability is not deleted.</div>
          : view === 'board'
            ? <LiveOddsVNextEventBoard events={visibleItems} books={matrix.books} oddsFormat={oddsFormat} fairSettings={fairSettings} onInspect={setSelectedRow} />
            : view === 'matrix'
            ? <LiveOddsVNextMatrix items={visibleItems} oddsFormat={oddsFormat} onInspect={setSelectedRow} />
            : <LiveOddsVNextExactTable groups={visibleItems} oddsFormat={oddsFormat} onInspect={setSelectedRow} />}
      <PageControls page={safePage} pageCount={pageCount} pageSize={pageSize} itemCount={items.length} setPage={setPage} />
      <LiveOddsVNextOfferDrawer row={selectedRow} categorySlug={category} oddsFormat={oddsFormat} onClose={() => setSelectedRow(null)} />
    </div>
  )
}
